import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  removeAlertLocally,
  resolveAlertRequest,
  activateNextRecurrenceRequest,
  addNotifiedAlert,
  addToPopoverAlertList,
} from '../redux/slices/mycore/alertsActions';
import { playNotificationSound } from '../utils/notificationSound';

/**
 * Hook for monitoring and triggering scheduled alert notifications
 * Checks every 1 minute if any scheduled alerts should be triggered
 * based on their scheduled_date and scheduled_time
 */
const useAlertNotifications = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const intervalRef = useRef(null);
  const [isRehydrated, setIsRehydrated] = useState(false);

  // Get alerts and rehydration status from Redux
  const { list: alerts } = useSelector((state) => {
    const mycoreState = state.mycore;
    const alertsState = mycoreState?.alerts || {};

    // Check if Redux Persist has finished rehydrating
    const persistState = state._persist;
    const rehydrated = persistState?.rehydrated || false;

    // Update rehydration state
    if (rehydrated && !isRehydrated) {
      setIsRehydrated(true);
    }

    return {
      list: alertsState.list || []
    };
  });

  /**
   * Check if an alert should be triggered now
   * @param {Object} alert - Alert object with trigger_timestamp_utc
   * @returns {boolean} - True if alert should trigger
   */
  const shouldTriggerAlert = (alert) => {
    if (!alert.trigger_timestamp_utc) {
      return false;
    }

    if (alert.status !== 'active') {
      return false;
    }

    try {
      const nowSeconds = Math.floor(Date.now() / 1000);
      const triggerTime = alert.trigger_timestamp_utc;
      return triggerTime <= nowSeconds;
    } catch (error) {
      console.error('Error checking alert trigger time:', error);
      return false;
    }
  };

  /**
   * Check all alerts and trigger notifications for due alerts
   * This function ONLY reads from Redux Persist - it does NOT fetch from backend
   */
  const checkAlerts = useCallback(() => {
    if (!alerts || alerts.length === 0) {
      return;
    }

    alerts.forEach((alert) => {
      if (shouldTriggerAlert(alert)) {
        // Play notification sound
        playNotificationSound();

        // Show browser notification if permission granted
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            new Notification(alert.title || 'Sporexpert Alert', {
              body: alert.message || 'You have a scheduled alert',
              icon: '/logo192.png',
              badge: '/logo192.png',
              tag: `alert-${alert.id}`, // Prevents duplicate notifications
              requireInteraction: false,
            });
          } catch (error) {
            console.error('Error showing browser notification:', error);
          }
        }

        // ADD to notified alerts list (for badge count)
        dispatch(addNotifiedAlert(alert.id));

        // ADD to popover alert list (with complete data for display)
        dispatch(addToPopoverAlertList(alert));

        // REMOVE alert from Redux persist
        dispatch(removeAlertLocally(alert.id));

        // Decide action based on recurrence type
        if (alert.recurrence_type === 'once') {
          // One-time: mark as resolved in backend
          dispatch(resolveAlertRequest(alert.id));
        } else {
          // Recurring: activate next occurrence in backend
          dispatch(activateNextRecurrenceRequest(alert.id));
        }
      }
    });
  }, [alerts, dispatch]); // Dependencies to ensure fresh values

  /**
   * Request browser notification permission
   */
  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        await Notification.requestPermission();
      } catch (error) {
        console.error('Error requesting notification permission:', error);
      }
    }
  };

  /**
   * Check client-server clock synchronization
   * Detects if client and server clocks are significantly out of sync
   */
  const checkServerSync = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_HOST_API}/api/v1/mycore/alerts/server-time/`);

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      const clientTimestamp = Math.floor(Date.now() / 1000);
      const serverTimestamp = data.server_timestamp_utc;
      const offsetSeconds = clientTimestamp - serverTimestamp;
      const offsetMinutes = Math.abs(Math.floor(offsetSeconds / 60));

      // Warn if clocks are more than 1 minute out of sync
      if (Math.abs(offsetSeconds) > 60) {
        const message = `Tu reloj está desfasado ${offsetMinutes} minutos. Las alertas pueden no sonar a tiempo.`;

        enqueueSnackbar(message, {
          variant: 'warning',
          autoHideDuration: 10000,
          anchorOrigin: { vertical: 'top', horizontal: 'center' }
        });
      }
    } catch (error) {
      // Don't block app functionality if sync check fails
    }
  };

  // NOTE: This hook NO LONGER fetches alerts
  // Fetching only happens in these cases:
  // 1. Tab change in Alerts.js (Active/History/All)
  // 2. Create alert
  // 3. Delete alert
  // 4. User login
  // This hook ONLY monitors Redux Persist and triggers notifications

  /**
   * Setup monitoring on mount
   * This hook ONLY monitors Redux Persist - it does NOT fetch alerts
   */
  useEffect(() => {
    // Request notification permission on first load
    requestNotificationPermission();

    // Check client-server clock synchronization
    checkServerSync();

    // Set up interval to check alerts every 30 seconds
    // NOTE: This ONLY checks Redux Persist - NO backend fetch
    intervalRef.current = setInterval(() => {
      if (isRehydrated) {
        checkAlerts();
      }
    }, 60000); // 60 seconds (1 minute)

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRehydrated, checkAlerts]); // Include checkAlerts dependency

  /**
   * Re-check alerts when the alerts list or notified IDs change
   * This handles both Redux Persist rehydration and new fetches
   * ONLY check if rehydration is complete
   */
  useEffect(() => {
    if (isRehydrated && alerts && alerts.length > 0) {
      checkAlerts();
    }
  }, [alerts, isRehydrated, checkAlerts]);

  return {
    // Expose utility functions if needed
    requestNotificationPermission,
  };
};

export default useAlertNotifications;
